// camera.ts — rear-camera capture for the scanner. Opens getUserMedia with the
// environment-facing camera and, per tracked frame, draws the video ONCE: the
// downscaled RGBA (long side ≤ 960 px) for the WASM tracker and the
// full-resolution ImageBitmap for a possible keyframe both come from that one
// draw, so a frame the tracker later flags as a keyframe is JPEG-encoded from
// exactly the pixels it tracked. (A separate createImageBitmap(video) call
// returned a later video frame — up to ~1 s late under load — so keyframe
// images disagreed with their poses by centimetres and wrecked the server's
// carving and training; see grab.) It also exposes the stream (the §15
// recorder records it) and measures the delivered frame rate (§15.1).

export const MAX_TRACK_LONG_SIDE = 960;

// trackingSize returns the processing size for a video of w×h.
export function trackingSize(w: number, h: number, maxLong = MAX_TRACK_LONG_SIDE): { width: number; height: number } {
  const long = Math.max(w, h);
  if (long <= maxLong) return { width: w, height: h };
  const s = maxLong / long;
  return { width: Math.round(w * s), height: Math.round(h * s) };
}

// GrabbedFrame is one video frame as the tracker sees it plus the
// full-resolution bitmap of the same pixels (for a keyframe JPEG).
export interface GrabbedFrame {
  image: ImageData;
  full: Promise<ImageBitmap | null>;
}

export class CameraError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CameraError';
  }
}

export class CameraCapture {
  private stream: MediaStream | null = null;
  private video: HTMLVideoElement | null = null;
  private canvas: HTMLCanvasElement = document.createElement('canvas');
  private ctx: CanvasRenderingContext2D | null = null;
  private fullCanvas: HTMLCanvasElement = document.createElement('canvas');
  private fullCtx: CanvasRenderingContext2D | null = null;

  get active(): boolean {
    return !!this.stream;
  }

  get videoWidth(): number {
    return this.video?.videoWidth ?? 0;
  }

  get videoHeight(): number {
    return this.video?.videoHeight ?? 0;
  }

  // track is the live video track (lens-profile fingerprint, §14).
  get track(): MediaStreamTrack | null {
    return this.stream?.getVideoTracks()[0] ?? null;
  }

  // mediaStream is the live stream (recorded by MediaRecorder, §15.2).
  get mediaStream(): MediaStream | null {
    return this.stream;
  }

  // settingsFps is the track's advertised getSettings().frameRate, if any.
  get settingsFps(): number | null {
    const fr = this.track?.getSettings?.().frameRate;
    return typeof fr === 'number' && fr > 0 ? fr : null;
  }

  // measureFps counts delivered video frames for ms milliseconds.
  measureFps(ms = 1500): Promise<number | null> {
    return this.video ? measureVideoFps(this.video, ms) : Promise.resolve(null);
  }

  // start attaches the rear camera to `video` and resolves once frames flow.
  async start(video: HTMLVideoElement): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new CameraError('This browser has no camera access (getUserMedia). Open the page over HTTPS on a phone.');
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      throw new CameraError(`Camera permission was refused or no camera is available (${msg}).`);
    }
    this.video = video;
    video.srcObject = this.stream;
    video.muted = true;
    video.playsInline = true;
    await video.play();
    if (!video.videoWidth) {
      await new Promise<void>((resolve) => video.addEventListener('loadedmetadata', () => resolve(), { once: true }));
    }
    const track = this.stream.getVideoTracks()[0];
    console.info('[openscan/camera] started', {
      width: video.videoWidth, height: video.videoHeight, label: track?.label, settings: track?.getSettings?.(),
    });
  }

  // grabFrame returns the current frame as RGBA at ≤ maxLong on its long side.
  grabFrame(maxLong = MAX_TRACK_LONG_SIDE): ImageData | null {
    return this.draw(maxLong)?.image ?? null;
  }

  // grab returns the current frame for the tracker together with the
  // full-resolution bitmap of the very same pixels. The bitmap's source (the
  // ImageData itself, or the full-res canvas the tracking frame was scaled
  // from) is copied synchronously inside this call, before anything can draw
  // a newer video frame, so the keyframe JPEG always matches the tracked pose.
  grab(maxLong = MAX_TRACK_LONG_SIDE): GrabbedFrame | null {
    const d = this.draw(maxLong);
    if (!d) return null;
    let full: Promise<ImageBitmap | null>;
    try {
      full = createImageBitmap(d.fullSource).catch((err: unknown) => {
        console.warn('[openscan/camera] createImageBitmap failed', err);
        return null;
      });
    } catch (err) {
      console.warn('[openscan/camera] createImageBitmap threw', err);
      full = Promise.resolve(null);
    }
    return { image: d.image, full };
  }

  // draw renders the current video frame once and returns the tracking
  // pixels plus the full-resolution source they were taken from.
  private draw(maxLong: number): { image: ImageData; fullSource: ImageData | HTMLCanvasElement } | null {
    const v = this.video;
    if (!v || !v.videoWidth || v.readyState < 2) return null;
    const vw = v.videoWidth, vh = v.videoHeight;
    const { width, height } = trackingSize(vw, vh, maxLong);
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
      this.ctx = null;
    }
    if (!this.ctx) this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    if (!this.ctx) return null;
    if (width === vw && height === vh) {
      // No downscale: the tracked ImageData IS the full-resolution frame.
      this.ctx.drawImage(v, 0, 0, width, height);
      const image = this.ctx.getImageData(0, 0, width, height);
      return { image, fullSource: image };
    }
    if (this.fullCanvas.width !== vw || this.fullCanvas.height !== vh) {
      this.fullCanvas.width = vw;
      this.fullCanvas.height = vh;
      this.fullCtx = null;
      console.info(`[openscan/camera] full-res keyframe canvas ${vw}x${vh}, tracking ${width}x${height}`);
    }
    if (!this.fullCtx) this.fullCtx = this.fullCanvas.getContext('2d');
    if (!this.fullCtx) return null;
    this.fullCtx.drawImage(v, 0, 0, vw, vh);
    this.ctx.drawImage(this.fullCanvas, 0, 0, width, height);
    return { image: this.ctx.getImageData(0, 0, width, height), fullSource: this.fullCanvas };
  }

  stop(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    if (this.video) this.video.srcObject = null;
    this.video = null;
    console.info('[openscan/camera] stopped');
  }
}

// encodeJpeg encodes a full-resolution bitmap as JPEG (keyframe upload).
export async function encodeJpeg(bitmap: ImageBitmap, quality = 0.92): Promise<Blob> {
  const c = document.createElement('canvas');
  c.width = bitmap.width;
  c.height = bitmap.height;
  const ctx = c.getContext('2d');
  if (!ctx) throw new CameraError('2D canvas unavailable for JPEG encoding');
  ctx.drawImage(bitmap, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) => c.toBlob(resolve, 'image/jpeg', quality));
  if (!blob) throw new CameraError('JPEG encoding failed');
  return blob;
}

type VideoFrameCallbackVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: (now: number, meta: { presentedFrames?: number }) => void) => number;
};

// measureVideoFps measures the camera's delivered frame rate over ms with
// requestVideoFrameCallback (one callback per presented frame), else with
// requestAnimationFrame (a display-rate upper bound). null when nothing ran.
export function measureVideoFps(video: HTMLVideoElement, ms = 1500): Promise<number | null> {
  const v = video as VideoFrameCallbackVideo;
  const viaVfc = typeof v.requestVideoFrameCallback === 'function';
  return new Promise((resolve) => {
    let n = 0;
    let t0 = -1;
    let last = -1;
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      const fps = n >= 2 && last > t0 ? ((n - 1) * 1000) / (last - t0) : null;
      console.debug(`[openscan/camera] measured ${fps === null ? 'no' : fps.toFixed(1)} fps via ${viaVfc ? 'requestVideoFrameCallback' : 'requestAnimationFrame'} (${n} callbacks)`);
      resolve(fps);
    };
    const tick = (now: number): void => {
      if (done) return;
      if (t0 < 0) t0 = now;
      last = now;
      n++;
      if (now - t0 >= ms) return finish();
      if (viaVfc) v.requestVideoFrameCallback!(tick);
      else requestAnimationFrame(tick);
    };
    if (viaVfc) v.requestVideoFrameCallback!(tick);
    else requestAnimationFrame(tick);
    globalThis.setTimeout(finish, ms * 2 + 500); // a paused video never calls back
  });
}
